import { Component, OnInit, signal, AfterViewInit, ViewChild, ElementRef, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import Quill from 'quill';
import { PostService, Post } from '../../../services/post';
import { AdminHeader } from '../../../services/admin-header';
import { environment } from '../../../../environments/environment';
import { ImageCropperModal } from '../../../components/image-cropper-modal/image-cropper-modal';

@Component({
  selector: 'app-post-editor',
  imports: [CommonModule, FormsModule, RouterLink, ImageCropperModal],
  templateUrl: './post-editor.html',
  styleUrl: './post-editor.css',
})
export class PostEditor implements OnInit, AfterViewInit, OnDestroy {
  private blogService = inject(PostService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);  private adminHeader = inject(AdminHeader);
  @ViewChild('editor') editorElement!: ElementRef;
  @ViewChild('imageCropper') imageCropper!: ImageCropperModal;
  private quill: Quill | null = null;

  selectedImageForCrop: File | null = null;

  // Signals
  isLoading = signal(false);
  isSaving = signal(false);
  errorMessage = signal('');
  successMessage = signal('');

  // Edit mode
  isEditMode = signal(false);
  blogId: number | null = null;

  // Store content to set after editor is initialized
  pendingContent: string | null = null;

  // Form data
  blog = {
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featured_image: null as File | null,
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    is_published: false,
    is_featured: false,
    tags: [] as string[]
  };

  // Preview
  featuredImagePreview: string | null = null;
  existingFeaturedImage: string | null = null;

  // Tags input
  tagInput = '';

  ngOnInit() {
    // Check if editing existing blog
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEditMode.set(true);
        this.blogId = parseInt(params['id']);
        this.loadBlog(this.blogId);
      } else {
        // For new blog, initialize editor after view is ready
        setTimeout(() => {
          this.initializeQuill();
        }, 0);
      }
    });

    // Set header actions
    this.setupHeaderActions();
  }

  ngOnDestroy() {
    // Clear header actions when component is destroyed
    this.adminHeader.clearHeaderConfig();
  }

  private setupHeaderActions() {
    this.adminHeader.setHeaderConfig({
      backButton: {
        show: true,
        callback: () => this.cancel()
      },
      actions: [
        {
          label: 'Save Draft',
          icon: 'M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4',
          callback: () => this.saveDraft(),
          variant: 'secondary',
          disabled: this.isSaving()
        },
        {
          label: 'Publish',
          icon: 'M5 13l4 4L19 7',
          callback: () => this.publishBlog(),
          variant: 'primary',
          disabled: this.isSaving()
        }
      ]
    });
  }

  ngAfterViewInit() {
    // Don't initialize here - will initialize after loading completes
  }

  private initializeQuill() {
    if (this.editorElement) {
      this.quill = new Quill(this.editorElement.nativeElement, {
        theme: 'snow',
        modules: {
          toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'align': [] }],
            ['blockquote', 'code-block'],
            ['link', 'image'],
            ['clean']
          ]
        },
        placeholder: 'Write your blog content here...'
      });

      // Update content on text change
      this.quill.on('text-change', () => {
        this.blog.content = this.quill?.root.innerHTML || '';
      });
    }
  }

  focusEditor() {
    if (this.quill) {
      this.quill.focus();
    }
  }

  private loadBlog(id: number) {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.blogService.getBlogById(id).subscribe({
      next: (response) => {
        if (response.status && response.data) {
          const blogData = response.data;

          this.blog = {
            title: blogData.title,
            slug: blogData.slug,
            excerpt: blogData.short_description || '',
            content: blogData.content,
            featured_image: null,
            meta_title: blogData.meta_title || '',
            meta_description: blogData.meta_description || '',
            meta_keywords: blogData.meta_keywords || '',
            is_published: blogData.is_published,
            is_featured: blogData.is_featured,
            tags: blogData.tags?.map((t: any) => t.name) || []
          };

          // Set existing image
          if (blogData.featured_image) {
            this.existingFeaturedImage = blogData.featured_image;
          }

          // Store content to set after editor is initialized
          this.pendingContent = blogData.content;
        } else {
          this.errorMessage.set(response.message || 'Failed to load blog');
        }
        this.isLoading.set(false);

        // Initialize editor after view is updated (when isLoading becomes false)
        setTimeout(() => {
          this.initializeQuill();
          if (this.pendingContent && this.quill) {
            this.quill.root.innerHTML = this.pendingContent;
            this.pendingContent = null;
          }
        }, 0);
      },
      error: (error) => {
        this.errorMessage.set('Error loading blog');
        console.error('Error loading blog:', error);
        this.isLoading.set(false);
      }
    });
  }

  onTitleChange() {
    // Auto-generate slug from title
    if (!this.isEditMode()) {
      this.blog.slug = this.blog.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    }
  }

  onFeaturedImageSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];

      // Validate file type
      if (!file.type.startsWith('image/')) {
        this.errorMessage.set('Please select a valid image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.errorMessage.set('Image size should not exceed 5MB');
        return;
      }

      // Open cropper modal
      this.selectedImageForCrop = file;

      // Reset input so re-selecting the same file triggers change event
      input.value = '';
      this.errorMessage.set('');

      // Wait for next tick so the cropper picks up the file
      setTimeout(() => this.imageCropper.open(), 0);
    }
  }

  onImageCropped(event: { file: File; preview: string }) {
    this.blog.featured_image = event.file;
    this.featuredImagePreview = event.preview;
    this.existingFeaturedImage = null;
    this.selectedImageForCrop = null;
  }

  onImageCropCancelled() {
    this.selectedImageForCrop = null;
  }

  addTag() {
    const tag = this.tagInput.trim();
    if (tag && !this.blog.tags.includes(tag)) {
      this.blog.tags = [...this.blog.tags, tag];
      this.tagInput = '';
    }
  }

  removeTag(tag: string) {
    this.blog.tags = this.blog.tags.filter(t => t !== tag);
  }

  onTagInputKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addTag();
    }
  }

  private prepareFormData(): FormData {
    const formData = new FormData();

    formData.append('title', this.blog.title);
    formData.append('slug', this.blog.slug);
    formData.append('short_description', this.blog.excerpt);
    formData.append('content', this.blog.content);

    if (this.blog.featured_image) {
      formData.append('featured_image', this.blog.featured_image);
    }

    formData.append('meta_title', this.blog.meta_title);
    formData.append('meta_description', this.blog.meta_description);
    formData.append('meta_keywords', this.blog.meta_keywords);
    formData.append('is_published', this.blog.is_published ? '1' : '0');
    formData.append('is_featured', this.blog.is_featured ? '1' : '0');

    // Add tags as array
    this.blog.tags.forEach((tag, index) => {
      formData.append(`tags[${index}]`, tag);
    });

    return formData;
  }

  saveBlog(publish: boolean = false) {
    // Validation
    if (!this.blog.title.trim()) {
      this.errorMessage.set('Title is required');
      return;
    }

    if (!this.blog.excerpt.trim()) {
      this.errorMessage.set('Excerpt is required');
      return;
    }

    if (!this.blog.content.trim()) {
      this.errorMessage.set('Content is required');
      return;
    }

    this.isSaving.set(true);
    this.setupHeaderActions(); // Update header to show loading state
    this.errorMessage.set('');
    this.successMessage.set('');

    // Set publish status
    this.blog.is_published = publish;

    // Prepare FormData
    const formData = this.prepareFormData();

    const saveObservable = this.isEditMode() && this.blogId
      ? this.blogService.updateBlog(this.blogId, formData)
      : this.blogService.createBlog(formData);

    saveObservable.subscribe({
      next: (response) => {
        if (response.status) {
          this.successMessage.set(
            this.isEditMode()
              ? 'Blog updated successfully!'
              : 'Blog created successfully!'
          );

          // Redirect after short delay
          setTimeout(() => {
            this.router.navigate(['/admin/blog']);
          }, 1500);
        } else {
          this.errorMessage.set(response.message || 'Failed to save blog');
        }
        this.isSaving.set(false);
        this.setupHeaderActions(); // Update header to remove loading state
      },
      error: (error) => {
        this.errorMessage.set('Error saving blog: ' + (error.error?.message || error.message));
        console.error('Error saving blog:', error);
        this.isSaving.set(false);
        this.setupHeaderActions(); // Update header to remove loading state
      }
    });
  }

  saveDraft() {
    this.saveBlog(false);
  }

  publishBlog() {
    this.saveBlog(true);
  }

  cancel() {
    if (confirm('Are you sure you want to discard changes?')) {
      this.router.navigate(['/admin/blog']);
    }
  }

  getImageUrl(path: string | null): string {
    if (!path) return '/images/placeholder.jpg';
    return path.startsWith('http') ? path : `${environment.storageUrl}/${path}`;
  }
}
